import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, tap, switchMap, withLatestFrom, pairwise } from 'rxjs';
import { signInClient } from '../actions/auth.actions';
import { AuthManagedStorageService } from '../auth-managed-storage.service';
import { AuthService } from '../auth.service';

@Injectable()
export class SignInClientEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly storage: AuthManagedStorageService,
    private readonly authService: AuthService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  setSignInClient$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(signInClient),
        tap(action => this.storage.updateStateItem('signedInClientId', action.signedInClientId))
      ),
    { dispatch: false }
  );

  rejectReassignmentOfSignInClient$ = createEffect(
    () => {
      // note: to relax this restriction would require reloading user attributes and permissions, and ensuring impersonation
      // is unset, etc, etc (hint: you would want to do that in SignInClientEffects class if you attempt that)
      // whilst doable, the intent here is that there is also an "in-app" client selection screen which allows the user to select
      // from a list of client's that are *already onboarded to app* to impersonate (for example, a support user).
      // This "in-app" screen would also allow the user to select which client they wish to sign in as, which would be
      // implemented to reload app with the selected client id as a query string parameter
      return this.authService.isSignedInClientValid$.pipe(
        filter(isValid => isValid),
        switchMap(() =>
          this.authService.signedInClientId$.pipe(
            pairwise(),
            filter(([prev, curr]) => prev !== curr)
          )
        ),
        tap(([prev, curr]) => {
          throw new Error(
            `Tried to change signed in client id from '${prev}' to '${curr}'. This library does not support changing the signed in client id during a session. Use AuthService.reloadWithSignInClient instead.`
          );
        })
      );
    },
    { dispatch: false }
  );

  rememberSignedInClient$ = createEffect(
    () =>
      this.authService.isSignedInClientValid$.pipe(
        filter(isValid => isValid),
        withLatestFrom(this.authService.signedInClientId$.pipe(filter((clientId): clientId is string => !!clientId))),
        tap(([_, clientId]) => {
          this.document.cookie = `${this.authService.config.validatedClientCookieName}=${encodeURIComponent(clientId)}; path=/; max-age=31536000`;
        })
      ),
    { dispatch: false }
  );
}
